// owner/screens/Units.js — «الوحدات» (/properties): every unit the owner owns, in the approved kit (no mock-up of its
// own; reviewed on dev). All units load at once (fine to 250, API_DATA_FETCHING_PATTERNS); the search box only
// narrows the loaded list. Status chips load in batches of 200 and show nothing until known, never a guess.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useLanguage from '../../hooks/useLanguage';
import { fill } from '../../utils/localeText';
import Icon from '../ui/Icon';
import { SkeletonBlocks, ErrorState, InlineError, EmptyState } from '../ui/States';
import { UnitThumb, UnitStatusChip, useUnitTypeLine } from '../ui/UnitBits';
import useOwnerFormat from '../ui/useOwnerFormat';
import { useOwnerUnits, useUnitStatuses } from '../api/ownerReads';

// Below this many units a search box is noise.
const SEARCH_FROM = 6;

const Units = () => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const typeLine = useUnitTypeLine();
  const units = useOwnerUnits();
  const [query, setQuery] = useState('');

  const unitList = useMemo(() => (Array.isArray(units.data) ? units.data : []), [units.data]);
  const statuses = useUnitStatuses(useMemo(() => unitList.map((u) => u._id), [unitList]));

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return unitList;
    return unitList.filter((u) => `${u.name || ''} ${u.propertyCode || ''}`.toLowerCase().includes(q));
  }, [unitList, query]);

  if (units.isLoading) return <SkeletonBlocks blocks={3} />;
  if (units.isError) return <ErrorState title={t('ownerApp.units.loadError')} onRetry={units.refetch} />;
  if (unitList.length === 0) {
    return <EmptyState icon="units" title={t('ownerApp.overview.noUnitsTitle')} hint={t('ownerApp.overview.noUnitsHint')} />;
  }

  return (
    <>
      <div className="o-page-title">
        <h1>{t('ownerApp.units.title')}</h1>
        <p className="o-sub">{fill(t('ownerApp.overview.unitsManaged'), { units: f.units(unitList.length) })}</p>
        <span className="o-underline" />
      </div>

      {unitList.length >= SEARCH_FROM && (
        <label className="o-search">
          <span className="o-visually-hidden">{t('ownerApp.units.search')}</span>
          <Icon name="search" size="sm" />
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('ownerApp.units.search')} />
        </label>
      )}

      <section className="o-section">
        {statuses.isError && (
          <div className="o-sheet" style={{ marginBottom: 12 }}>
            <InlineError text={t('ownerApp.units.statusError')} onRetry={statuses.refetch} />
          </div>
        )}
        <div className="o-sheet">
          {shown.length === 0
            ? <p className="o-empty-row">{t('ownerApp.units.noMatch')}</p>
            : (
              <ul className="o-rows o-units-grid">
                {shown.map((u, i) => (
                  <li key={u._id}>
                    <Link to={`/properties/${u._id}`} className="o-row">
                      <UnitThumb photo={u.bookingPageSettings?.primaryPhoto} index={i} />
                      <div className="o-body">
                        <p className="o-t">{u.name}</p>
                        <p className="o-m">
                          {u.propertyCode && <span className="o-code">{u.propertyCode}</span>}
                          {typeLine(u).map((part) => <span key={part}>{part}</span>)}
                          {u.city && <span>{u.city}</span>}
                        </p>
                      </div>
                      <div className="o-end"><UnitStatusChip status={statuses.byId[u._id]?.status} /></div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </section>
    </>
  );
};

export default Units;
